import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { supabase } from '../lib';
import { setAuthToken } from '../services/api';

const AuthContext = createContext({
  user: null,
  session: null,
  initializing: true,
  passwordRecovery: false,
  login: async () => {
    throw new Error('AuthProvider not mounted');
  },
  signup: async () => {
    throw new Error('AuthProvider not mounted');
  },
  logout: async () => {
    throw new Error('AuthProvider not mounted');
  },
  resetPassword: async () => {
    throw new Error('AuthProvider not mounted');
  },
  updatePassword: async () => {
    throw new Error('AuthProvider not mounted');
  },
  signInWithOAuth: async () => {
    throw new Error('AuthProvider not mounted');
  },
});

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [passwordRecovery, setPasswordRecovery] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadSession = async () => {
      const { data, error } = await supabase.auth.getSession();
      if (!mounted) {
        return;
      }

      if (error) {
        console.error('[auth] Failed to fetch session', error);
      }
      setSession(data?.session ?? null);
      setInitializing(false);
    };

    loadSession();

    const { data: listener } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!mounted) {
        return;
      }
      if (event === 'PASSWORD_RECOVERY') {
        setPasswordRecovery(true);
      } else if (event === 'SIGNED_OUT' || event === 'SIGNED_IN') {
        setPasswordRecovery(false);
      }

      setSession(newSession);
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    setAuthToken(session?.access_token ?? null);
  }, [session?.access_token]);

  const login = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      throw error;
    }
    return data;
  }, []);

  const buildRedirectTo = useCallback(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const url = new URL(window.location.origin);
    url.pathname = '/reset-password';
    return url.toString();
  }, []);

  const signup = useCallback(
    async (email, password) => {
      const signupOptions =
        typeof window !== 'undefined'
          ? { emailRedirectTo: buildRedirectTo() ?? window.location.origin }
          : undefined;

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: signupOptions,
      });
      if (error) {
        throw error;
      }
      return data;
    },
    [buildRedirectTo],
  );

  const resetPassword = useCallback(
    async (email) => {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: buildRedirectTo(),
      });
      if (error) {
        throw error;
      }
      return data;
    },
    [buildRedirectTo],
  );

  const updatePassword = useCallback(async (newPassword) => {
    const { data, error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      throw error;
    }
    setPasswordRecovery(false);
    return data;
  }, []);

  const signInWithOAuth = useCallback(async (provider) => {
    const redirectTo =
      typeof window !== 'undefined'
        ? `${window.location.origin}/dashboard`
        : undefined;

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo },
    });
    if (error) {
      throw error;
    }
    return data;
  }, []);

  const logout = useCallback(async () => {
    const { error } = await supabase.auth.signOut({ scope: "local" });
    if (error && error.name !== "AuthSessionMissingError") {
      throw error;
    }
    setSession(null);
    setAuthToken(null);
  }, []);

  const value = useMemo(
    () => ({
      user: session?.user ?? null,
      session,
      initializing,
      passwordRecovery,
      login,
      signup,
      resetPassword,
      updatePassword,
      signInWithOAuth,
      logout,
    }),
    [session, initializing, passwordRecovery, login, signup, resetPassword, updatePassword, signInWithOAuth, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;

